import { BitmapCoordinatesRenderingScope, CanvasRenderingTarget2D } from 'fancy-canvas'
import {
    DrawingDisplayType,
    DrawingMode,
    DrawingOptions,
    DrawingSettingGroup,
    DrawingSettingItem,
    DrawingTypes,
    LabelAxisViewConfig,
    Point,
    PointCoordinates,
    SettingValues
} from './drawing.interface'
import DrawingPrimitive from './drawing.primitive'
import {
    AutoscaleInfo,
    BarPrice,
    Coordinate,
    IChartApi,
    ISeriesApi,
    Logical,
    MouseEventParams,
    PrimitiveHoveredItem,
    Time
} from 'lightweight-charts'
import DrawingPaneView from './drawing.paneView'
import DrawingHandlePaneView from './drawing.handle.paneView'
import { HANDLER_RADIUS, HANDLER_STROKE_WIDTH, SNAP_THRESHOLD } from './drawing.handler.constants'
import { determineChartX, determineChartY, getHitThreshold, isCoarsePointer, scaledCoordinate } from './drawing.helper'
import { findTouchDrawChartPane, getTouchDrawStep, TOUCH_DRAW_TAP_MAX_MS, TOUCH_DRAW_TAP_SLOP_PX } from './drawing.touchDraw'
import { deepClone } from '@mui/x-data-grid/utils/utils'
import DrawingPaneAxisViewManager from './drawing.paneAxisViewManager'
import DrawingAxisLabelView from './drawing.axisLabelView'
import type Chart from '../chart'
import { ChartEvents } from '../chart.events'
import { debounce } from 'throttle-debounce'
import { getTimeIndex } from '../../../util/getTimeIndex'
import { convertToUTC } from '../../../util/dateTime'
import { ActionType } from '../../UndoRedoFunctonalities/undoRedoActionTypes'
import { primaryDrawingColor } from '../../../assets/colors'
import {
    findUserAlertForDrawingId,
    isAlertableDrawingType,
} from '../../../util/alerts'
import { UserAlert_Status } from '../../../proto/generated/nexus'
import { drawDrawingAlertIcon } from './drawing.alertIcon'

class DrawingBase<O extends Record<string, any>, C extends Record<string, any>, S extends Record<string, any>> {
    id: string
    displayType: DrawingDisplayType
    referenceDrawingId?: string
    options: O
    _state: S
    _active = false
    _softActiveMode = false
    fixedDrawing = false;
    visible = true
    _chart: Chart
    _chartApi: IChartApi
    _seriesId = ''
    _paneId = ''
    _seriesMap: Record<string, ISeriesApi<any>> = {}
    _settings: C
    _paneElement?: HTMLElement
    linked: boolean
    mode?: DrawingMode
    editing: boolean = false
    magnetMode: boolean
    primitive?: DrawingPrimitive
    primitiveAttached = false
    _destroyed = false
    paneViews: DrawingPaneView[] = []
    resizeObserver: ResizeObserver
    _firstClick = true
    _hovered: boolean = false
    _chartBounds?: DOMRect
    _mousePressed = false
    activeElementId = ''
    moveStartMouseLocation?: PointCoordinates
    moveStartHandles?: Point[]
    axisViewManager: DrawingPaneAxisViewManager
    availableSettings: Record<string, DrawingSettingItem<C, SettingValues>> = {}
    lastAddedPoint?: Point
    /**
     * When true, a DRAW-mode click on the same spot as the previously committed point is
     * ignored instead of added. Tools whose shape degenerates with two coincident points
     * (lines) opt in; tools that use "click the last point again" to finish (paths) must not.
     */
    protected ignoreRepeatedPoint = false
    previewCursorPosition?: PointCoordinates
    paneIndex: number = 0
    subscribedPaneIndex: number | undefined = undefined
    /** DRAW mode: mousedown/mouseup (or touch) on each pane so clicks work without relying on crosshair paneIndex alone */
    private _drawModePanePointerCleanups: Array<() => void> = []
    /** Touch cursor drawing (see drawing.touchDraw.ts). Cursor is in pane CSS px. */
    private _touchCursor?: PointCoordinates
    private _touchCursorActive = false
    private _touchCursorCleanups: Array<() => void> = []
    private _touchGesture?: { startX: number; startY: number; cursorX: number; cursorY: number; startTime: number; moved: boolean }
    private _touchPointsPlaced = 0
    private _touchCursorMoved = false
    private _touchPrevLayout?: { handleScroll: unknown; handleScale: unknown }
    /** A touch that started on a sibling chart is in flight and is being swallowed. */
    private _foreignTouchActive = false
    onMouseDownTime = new Date()
    onDestroy = (id: string, publish?: boolean) => {
    }
    onFocusChange = (id: string, focused: boolean) => {
    }
    onDrawingInactive = () => {
    }
    onDrawingActive = (drawingType: DrawingTypes) => {
    }
    onDrawingComplete = (id: string) => {
    }
    onDrawingUpdate = (id: string) => {
    }
    canvasWidth = 0
    canvasHeight = 0
    horizontalPixelRatio = 0
    verticalPixelRatio = 0
    private _cachedPrimarySeries: ISeriesApi<any> | null = null;
    private _cacheInvalidated = true;
    private _lastChartCompareMode: boolean | undefined = undefined;

    constructor({
                    id,
                    displayType,
                    referenceDrawingId,
                    options,
                    chart,
                    settings,
                    initialState,
                    magnetMode,
                    paneId,
                    seriesId,
                    onDestroy,
                    onFocusChange,
                    onDrawingInactive,
                    onDrawingActive,
                    onDrawingComplete,
                    onDrawingUpdate
                }: DrawingOptions<O, C, S>) {
        this.displayType = displayType;
        this.options = { ...options }
        this.id = id
        this.referenceDrawingId = referenceDrawingId
        this.linked = !!referenceDrawingId
        this._chart = chart
        this.magnetMode = magnetMode
        this._chartApi = chart.chartApi
        this._state = initialState
        this._paneId = paneId || ''
        this._seriesId = seriesId || ''
        this.resizeObserver = new ResizeObserver(this.onPaneResize)



        if (this._paneId && this._seriesId && this._chartApi) {
            const api = this._chart.getSeriesApi(this._paneId, this._seriesId)
            if (!api) {
                console.warn('DrawingBase: Series not found for drawing', this.id, 'paneId:', this._paneId, 'seriesId:', this._seriesId)
            } else {
                this._seriesMap[this.id] = api
                this.setPaneElement(this._paneId)
            }
        }
        if (Object.keys(initialState).length) {
            this.mode = 'EDIT'
        }
        this.onDestroy = onDestroy
        this.onFocusChange = onFocusChange
        this.onDrawingInactive = onDrawingInactive
        this.onDrawingActive = onDrawingActive
        this.onDrawingComplete = onDrawingComplete
        this.onDrawingUpdate = onDrawingUpdate
        this.primitive = new DrawingPrimitive({
            drawing: this
        })
        this.paneViews = [new DrawingPaneView({
            drawing: this,
            id: 'primary'
        })]
        this.axisViewManager = new DrawingPaneAxisViewManager({ drawing: this })
        this._chartApi && this.mode == 'EDIT' && this.subscribeClick()
        this._chartApi && this.mode == 'EDIT' && this.subscribeDblClick()
        this._firstClick = this.referenceDrawingId ? false : true
        this.addMouseLeaveHandler()
        this._settings = {} as C
        setTimeout(() => {
            if (!this._chartApi || (this._chart as any).destroyed || !this.primitive) return
            this._settings = this._createSettings({
                ...settings
            })
            if (this.displayType === DrawingDisplayType.INDICATOR) {
                this.onSettingsUpdate(this._settings)
            }
            if (this._paneId && this._seriesId) {
                this.ensurePrimitiveAttachedToSeries()
                this.updateDrawing()
            }
            if(this.mode === 'EDIT') {
                this.loadIntialState()
            }
        }, 0)
        this.onMouseDownTime = new Date()
        this._invalidateSeriesCache()
    }

    /**
     * Resolves `getSeriesApi` after pane/series init and attaches the primitive once.
     * Retries briefly for `fixedDrawing` tools (e.g. chart alerts) when the series is not ready on the first tick.
     */
    private ensurePrimitiveAttachedToSeries(attempt = 0): void {
        if (
            this._destroyed ||
            !this._chartApi ||
            (this._chart as any).destroyed ||
            !this.primitive ||
            !this._paneId ||
            !this._seriesId
        )
            return
        if (!this.getSeries()) {
            const api = this._chart.getSeriesApi(this._paneId, this._seriesId)
            if (api) {
                this._seriesMap[this.id] = api
                this.setPaneElement(this._paneId)
            }
        }
        const series = this.getSeries()
        if (series && this.primitive && !this.primitiveAttached) {
            try {
                series.attachPrimitive(this.primitive)
                this.primitiveAttached = true
            } catch (e) {
                console.warn('DrawingBase: Failed to attach primitive, chart may have been destroyed', e)
            }
        } else if (!series && this.fixedDrawing && attempt < 15) {
            setTimeout(() => this.ensurePrimitiveAttachedToSeries(attempt + 1), 80)
        }
    }

    _createSettings = (config: Partial<C>) => {

        const availableSettings = this.getAvailableSettings()

        const settings: C = availableSettings.reduce((acc, group) => {
            group.settings.forEach((setting) => {
                if (acc[setting.name]) throw `Duplicate setting name ${setting.name.toString()}`
                acc[setting.name] = setting.defaultValue
            })
            return {
                ...acc
            }
        }, {} as any)
        return {
            ...settings,
            ...config
        }
    }

    activate() {
        if (!this.mode) {
            this.mode = 'DRAW'
            if (this.referenceDrawingId) return
            this._onDrawingActive()
            if (!this._chartApi) return
            if (this._chart.touchEnabled) {
                // Mobile: drag moves a crosshair cursor, tap commits — see _startTouchCursorDrawing.
                this._startTouchCursorDrawing()
                return
            }
            this._chartApi.subscribeCrosshairMove(this._onMouseMovePaneIndexTracking)
            this.addMouseMoveHandler()
            this._attachDrawModePointerListeners()
        }
    }

    deactivate() {
        if (this.mode === 'DRAW') {
            this.mode = undefined
            if (this._touchCursorActive) {
                this._stopTouchCursorDrawing()
            } else {
                this._chart.updateLayoutOptions({
                    handleScroll: true
                })
            }
            this.destroy()
        }
    }

    // ── Touch cursor drawing ────────────────────────────────────────────────────────────────────
    // lightweight-charts binds its own touch gestures (pan, long-press tracking mode, tap→click) to
    // the pane canvas. While a drawing is being placed we take over touch entirely with capture-phase
    // listeners on the pane elements so the chart neither pans nor moves the crosshair under us.

    private _startTouchCursorDrawing() {
        if (!this._chartApi || this._touchCursorActive) return
        this._touchCursorActive = true
        this._touchPointsPlaced = 0
        this._touchCursorMoved = false
        this._touchGesture = undefined

        // options() returns the live internal object and applyOptions merges into it in place —
        // snapshot by value or the lock below would overwrite what we restore later.
        const opts = this._chartApi.options()
        this._touchPrevLayout = {
            handleScroll: JSON.parse(JSON.stringify(opts.handleScroll ?? true)),
            handleScale: JSON.parse(JSON.stringify(opts.handleScale ?? true))
        }
        this._chart.updateLayoutOptions({ handleScroll: false, handleScale: false })

        this._syncDrawPaneFromIndex(this.paneIndex || 0)

        const elements = [...(this._chartApi.getPaneElements() || [])] as HTMLElement[]
        elements.forEach((el, paneIndex) => {
            const onStart = (e: TouchEvent) => this._onTouchCursorStart(e, paneIndex)
            const onMove = (e: TouchEvent) => this._onTouchCursorMove(e)
            const onEnd = (e: TouchEvent) => this._onTouchCursorEnd(e)
            const onCancel = (e: TouchEvent) => this._onTouchCursorCancel(e)
            el.addEventListener('touchstart', onStart, { capture: true, passive: false })
            el.addEventListener('touchmove', onMove, { capture: true, passive: false })
            el.addEventListener('touchend', onEnd, { capture: true, passive: false })
            el.addEventListener('touchcancel', onCancel, { capture: true, passive: true })
            this._touchCursorCleanups.push(() => {
                el.removeEventListener('touchstart', onStart, { capture: true })
                el.removeEventListener('touchmove', onMove, { capture: true })
                el.removeEventListener('touchend', onEnd, { capture: true })
                el.removeEventListener('touchcancel', onCancel, { capture: true })
            })
        })

        // Sibling charts (multi-chart layouts): hand the cursor over on the first point, swallow taps after.
        document.addEventListener('touchstart', this._onForeignTouchStart, { capture: true, passive: false })
        document.addEventListener('touchmove', this._onForeignTouchMove, { capture: true, passive: false })
        document.addEventListener('touchend', this._onForeignTouchEnd, { capture: true, passive: false })
        document.addEventListener('touchcancel', this._onForeignTouchEnd, { capture: true, passive: true })
        this._touchCursorCleanups.push(() => {
            document.removeEventListener('touchstart', this._onForeignTouchStart, { capture: true })
            document.removeEventListener('touchmove', this._onForeignTouchMove, { capture: true })
            document.removeEventListener('touchend', this._onForeignTouchEnd, { capture: true })
            document.removeEventListener('touchcancel', this._onForeignTouchEnd, { capture: true })
        })

        // Start with the cursor in the middle of the pane so it is visible before the first drag.
        const bounds = this._paneElement?.getBoundingClientRect()
        if (bounds) {
            this._setTouchCursor(bounds.width / 2, bounds.height / 2, false)
        }
        this._publishTouchStep()
    }

    /**
     * Put the touch cursor at pane-local (x, y) on `paneIndex`, as the user's own move. Used when the
     * drawing is handed over from a sibling chart so the cursor appears where the user tapped.
     */
    placeTouchCursor(paneIndex: number, x: number, y: number) {
        if (!this._touchCursorActive || !this._firstClick) return
        if (paneIndex !== this.paneIndex) this._syncDrawPaneFromIndex(paneIndex)
        this._setTouchCursor(x, y, true)
    }

    private _onForeignTouchStart = (event: TouchEvent) => {
        if (event.touches.length !== 1) return
        const hit = findTouchDrawChartPane(event.target, this._chart)
        if (!hit) return
        // Never let the sibling chart see this gesture (no pan, no click, no crosshair).
        event.stopPropagation()
        if (event.cancelable) event.preventDefault()
        this._foreignTouchActive = true
        // Once a point is placed here, taps on other charts are ignored.
        if (!this._firstClick) return

        const touch = event.touches[0]
        const paneEl = (hit.chart.chartApi.getPaneElements()[hit.paneIndex] ?? null) as HTMLElement | null
        const rect = paneEl?.getBoundingClientRect()
        const type = this.getDrawingType()
        const options = { ...this.options }
        const settings = { ...this._settings }
        const target = hit.chart

        // Aborting destroys this drawing and ends its touch session (listeners, scroll lock, banner).
        this._chart.drawingManager.abort()
        const handedOver = target.drawingManager.addDrawing(type, DrawingDisplayType.DRAWING, options, settings)
        if (handedOver && rect) {
            handedOver.placeTouchCursor(hit.paneIndex, touch.clientX - rect.left, touch.clientY - rect.top)
        }
    }

    private _onForeignTouchMove = (event: TouchEvent) => {
        if (!this._foreignTouchActive) return
        event.stopPropagation()
        if (event.cancelable) event.preventDefault()
    }

    private _onForeignTouchEnd = (event: TouchEvent) => {
        if (!this._foreignTouchActive) return
        this._foreignTouchActive = false
        event.stopPropagation()
        if (event.cancelable) event.preventDefault()
    }

    private _stopTouchCursorDrawing() {
        if (!this._touchCursorActive) return
        this._touchCursorActive = false
        this._touchCursorCleanups.forEach((fn) => fn())
        this._touchCursorCleanups = []
        this._touchGesture = undefined
        this._touchCursor = undefined
        this.previewCursorPosition = undefined
        if (this._chartApi && !(this._chart as any).destroyed) {
            try {
                this._chart.updateLayoutOptions({
                    handleScroll: (this._touchPrevLayout?.handleScroll as any) ?? true,
                    handleScale: (this._touchPrevLayout?.handleScale as any) ?? true
                })
                this._chartApi.clearCrosshairPosition()
            } catch (e) {
                console.warn('DrawingBase: failed to restore chart options after touch drawing', e)
            }
        }
        this._touchPrevLayout = undefined
        this._chart.publishEvent(ChartEvents.DRAWING_TOUCH_STEP, null)
    }

    private _publishTouchStep() {
        if (!this._touchCursorActive) return
        this._chart.publishEvent(
            ChartEvents.DRAWING_TOUCH_STEP,
            getTouchDrawStep(this.id, this.getDrawingType(), this._touchPointsPlaced, this._touchCursorMoved)
        )
    }

    private _onTouchCursorStart = (event: TouchEvent, paneIndex: number) => {
        event.stopPropagation()
        if (event.touches.length !== 1) {
            // Ignore pinch / multi-finger; the chart is locked anyway.
            this._touchGesture = undefined
            return
        }
        const touch = event.touches[0]
        if (this._firstClick) {
            // Before the first point the drawing follows whichever pane is touched: this tap only
            // relocates the cursor to where the finger landed (it does not commit a point).
            if (paneIndex !== this.paneIndex) {
                this._syncDrawPaneFromIndex(paneIndex)
                const rect = this._paneElement?.getBoundingClientRect()
                if (rect) this._setTouchCursor(touch.clientX - rect.left, touch.clientY - rect.top, true)
                this._touchGesture = undefined
                return
            }
        } else if (paneIndex !== this.paneIndex) {
            // Subsequent points must stay on the pane the drawing started in.
            return
        }
        const bounds = this._paneElement?.getBoundingClientRect()
        const cursor = this._touchCursor ?? { x: (bounds?.width ?? 0) / 2, y: (bounds?.height ?? 0) / 2 }
        this._touchGesture = {
            startX: touch.clientX,
            startY: touch.clientY,
            cursorX: cursor.x,
            cursorY: cursor.y,
            startTime: Date.now(),
            moved: false
        }
    }

    private _onTouchCursorMove = (event: TouchEvent) => {
        event.stopPropagation()
        if (event.cancelable) event.preventDefault()
        const gesture = this._touchGesture
        if (!gesture || event.touches.length !== 1) return
        const touch = event.touches[0]
        const dx = touch.clientX - gesture.startX
        const dy = touch.clientY - gesture.startY
        if (!gesture.moved && Math.hypot(dx, dy) < TOUCH_DRAW_TAP_SLOP_PX) return
        gesture.moved = true
        // Relative drag: the cursor keeps its offset from the finger so it is never hidden under it.
        this._setTouchCursor(gesture.cursorX + dx, gesture.cursorY + dy, true)
    }

    private _onTouchCursorEnd = (event: TouchEvent) => {
        event.stopPropagation()
        // Suppress the compatibility mouse events (mousedown/click) a tap would otherwise synthesize.
        if (event.cancelable) event.preventDefault()
        const gesture = this._touchGesture
        this._touchGesture = undefined
        if (!gesture) return
        const isTap = !gesture.moved && Date.now() - gesture.startTime < TOUCH_DRAW_TAP_MAX_MS
        if (isTap) this._commitTouchCursorPoint()
    }

    private _onTouchCursorCancel = (event: TouchEvent) => {
        event.stopPropagation()
        this._touchGesture = undefined
    }

    /** Move the cursor (clamped to the pane) and refresh the crosshair + preview geometry. */
    private _setTouchCursor(x: number, y: number, byUser: boolean) {
        if (!this._chartApi || !this._paneElement) return
        const bounds = this._paneElement.getBoundingClientRect()
        const clampedX = Math.min(Math.max(x, 0), Math.max(bounds.width - 1, 0))
        const clampedY = Math.min(Math.max(y, 0), Math.max(bounds.height - 1, 0))

        const time = this.coordinateToTime(clampedX as Coordinate)
        let price = this.coordinateToPrice(clampedY as Coordinate)
        // Outside the time range (far-right whitespace) there is nothing to anchor to — keep the last position.
        if (!time || price === null || price === undefined) return

        let cursorY = clampedY
        if (this.magnetMode) {
            price = this.getSnapPoint(time, price, clampedY)
            cursorY = (this.priceToCoordinate(price) ?? clampedY) as number
        }
        // Snap horizontally to the bar the cursor resolves to, like the crosshair does.
        const cursorX = (this.timeToCoordinate(time) ?? clampedX) as number

        this._touchCursor = { x: cursorX, y: cursorY }
        if (byUser && !this._touchCursorMoved) {
            this._touchCursorMoved = true
            this._publishTouchStep()
        }

        try {
            this._chartApi.setCrosshairPosition(price, time, this.getSeries())
        } catch (e) {
            /* series may not be attached yet */
        }

        if (this._firstClick) {
            this.previewCursorPosition = { x: cursorX, y: cursorY }
            this.updateDrawing()
        } else {
            this._onAddPoint(time, price, { x: cursorX, y: cursorY }, true)
        }
    }

    /** Commit a point at the cursor — same path a desktop click takes. */
    private _commitTouchCursorPoint() {
        if (!this._chartApi || !this._touchCursor || this.mode !== 'DRAW') return
        const { x, y } = this._touchCursor
        const time = this.coordinateToTime(x as Coordinate)
        if (!time) return
        const synthetic = {
            hoveredObjectId: undefined,
            hoveredSeries: undefined,
            logical: true,
            paneIndex: this.paneIndex,
            point: { x, y },
            seriesData: undefined,
            sourceEvent: undefined,
            time
        }
        this._onClick(synthetic as any, false)
        if (this.mode !== 'DRAW' || !this._touchCursorActive) return // tool completed → onDrawingEnd stopped us
        this._touchPointsPlaced += 1
        this._touchCursorMoved = false
        // Keep the preview attached to the cursor for the next point.
        this._onAddPoint(time, this.coordinateToPrice(y as Coordinate) as number, { x, y }, true)
        this._publishTouchStep()
    }

    _getHandles = (): Point[] => {
        if (!this._active && this.mode === 'EDIT') return []
        return this.getHandles()
    }

    getPrimitive() {
        return this.primitive
    }

    setPaneElement(paneId: string, paneElement?: HTMLElement) {
        if (paneElement) {
            this._paneElement = paneElement
        } else {
            this._paneElement = this._chart.getPaneElementById(paneId)
        }
        if (!this._chartBounds && this._paneElement) this._chartBounds = this?._paneElement?.getBoundingClientRect()
        if (this._chartBounds) {
            this.canvasWidth = this._chartBounds.width * window.devicePixelRatio
            this.canvasHeight = this._chartBounds.height * window.devicePixelRatio
        }
        if (this._paneElement instanceof Element) {
            this.resizeObserver.observe(this._paneElement);
        } else {
            console.warn("ResizeObserver: tried to observe invalid element:", this._paneElement);
        }
    }

    onPaneResize = debounce(100, (entries: ResizeObserverEntry[]) => {
        for (const entry of entries) {
            this._chartBounds = entry.target.getBoundingClientRect()
        }
    })

    subscribeVisibleLogicalRange() {
        if (!this._chartApi) return
        this._chartApi.timeScale().subscribeVisibleLogicalRangeChange(this.onVisibleLogicalRangeChange)
    }

    unsubscribeVisibleLogicalRange() {
        if (!this._chartApi) return
        this._chartApi.timeScale().unsubscribeVisibleLogicalRangeChange(this.onVisibleLogicalRangeChange)
    }

    subscribeClick() {
        if (!this._chartApi) return
        this._chartApi.subscribeClick(this._onClick)
    }

    unsubscribeDblClick() {
        if (!this._chartApi) return
        this._chartApi.unsubscribeDblClick(this._onDblClick)
    }

    subscribeDblClick() {
        if (!this._chartApi) return
        this._chartApi.subscribeDblClick(this._onDblClick)
    }

    unsubscribeClick() {
        if (!this._chartApi) return
        this._chartApi.unsubscribeClick(this._onClick)
    }

    addMouseLeaveHandler() {
        if (this.getPaneElement()) {
            this.getPaneElement().addEventListener('mouseleave', this._onMouseLeave)
            const children = this.getPaneElement().children
            for (let i = 0; i < children.length; i++) {
                children.item(i)?.addEventListener('mouseenter', this._onMouseLeave)
            }
        }
    }

    removeMouseLeaveHandler() {
        if (this.getPaneElement()) {
            this.getPaneElement().removeEventListener('mouseleave', this._onMouseLeave)
            const children = this.getPaneElement().children
            for (let i = 0; i < children.length; i++) {
                children.item(i)?.removeEventListener('mouseenter', this._onMouseLeave)
            }
        }
    }

    addMouseMoveHandler() {
        if (!this._chartApi) return
        this._chartApi.subscribeCrosshairMove(this._onMouseMove)
        // this.getPaneElement().addEventListener('mousemove', this._onMouseMove)
    }

    removeMouseMoveHandler() {
        if (!this._chartApi) return
        this._chartApi.unsubscribeCrosshairMove(this._onMouseMove)
        // this.getPaneElement().removeEventListener('mousemove', this._onMouseMove)
    }

    addMouseDownHandler() {
        if(this._chart.touchEnabled) {
            this.getPaneElement() && this.getPaneElement().addEventListener('touchstart', this._onMouseDown, { passive: true })
        } else {
            this.getPaneElement() && this.getPaneElement().addEventListener('mousedown', this._onMouseDown)
        }
    }

    removeMouseDownHandler() {
        if(this._chart.touchEnabled) {
            this.getPaneElement() && this.getPaneElement().removeEventListener('touchstart', this._onMouseDown)
        } else {
            this.getPaneElement() && this.getPaneElement().removeEventListener('mousedown', this._onMouseDown)
        }
    }

    addGlobalMouseUpHandler() {
        if(this._chart.touchEnabled) {
            document.addEventListener('touchend', this._globalOnMouseUp, { passive: true })
        } else {
            document.addEventListener('mouseup', this._globalOnMouseUp)
        }
    }

    removeGlobalMouseUpHandler() {
        if(this._chart.touchEnabled) {
            document.removeEventListener('touchend', this._globalOnMouseUp)
        } else {
            document.removeEventListener('mouseup', this._globalOnMouseUp)
        }
    }

    onVisibleLogicalRangeChange = () => {
        this.updateDrawing()
    }
    private _onMouseMove = (event: MouseEventParams<Time>) => {
        this.onMouseMove(event)
    }

    private _onMouseLeave = () => {
        this.hitTest(NaN, NaN)
    }

    toggleFocus(focus: boolean, publish = true, softActiveMode = false) {
        this._active = focus
        this._softActiveMode = softActiveMode
        this.updateDrawing(false)
        !this._softActiveMode && this._active && this.addMouseDownHandler()
        !this._softActiveMode && !this._active && this.removeMouseDownHandler()
        publish && this._chart.publishDrawingEvent('focused', {
            id: this.id,
            focused: focus,
            referenceId: this.referenceDrawingId
        }, true)
        publish && this.onFocusChange(this.id, this._active)
    }

    getSeries() {
        return this._seriesMap[this.id]
    }

    getPaneElement(): HTMLElement {
        return this._paneElement!
    }

    private _onDblClick = (event: MouseEventParams) => {
        this._onClick(event, true)
        if (this.mode === 'EDIT') {
            this.openDrawingSettings()
        }
    }

    /**
     * Align pane element, series, and (before the first point) the drawing primitive with the given pane index.
     * Re-attaches the primitive when the user moves or clicks a different pane before placing the first point.
     */
    private _syncDrawPaneFromIndex(paneIndex: number): boolean {
        if (!this._chartApi) return false
        const paneId = this._chart.getPaneIdByIndex(paneIndex)
        const paneElements = this._chartApi.getPaneElements()
        const paneElement = paneElements[paneIndex]
        if (!paneId || !paneElement) return false
        this.paneIndex = paneIndex
        this.setPaneElement(paneId, paneElement)

        if (this.mode !== 'DRAW' || !this._firstClick || !this.primitive) {
            return true
        }

        const [seriesId, series] = this._chart.getDrawingSeries(paneId)
        if (!seriesId || !series) return true

        const alreadyOk =
            this.primitiveAttached &&
            this._paneId === paneId &&
            this._seriesId === seriesId &&
            this.getSeries() === series
        if (alreadyOk) return true

        const prevSeries = this.primitiveAttached ? this.getSeries() : undefined
        if (prevSeries && this.primitive) {
            try {
                prevSeries.detachPrimitive(this.primitive)
            } catch (e) {
                console.warn('DrawingBase: Failed to detach primitive when switching pane', e)
            }
            this.primitiveAttached = false
        }

        this._seriesId = seriesId
        this._paneId = paneId
        this._seriesMap[this.id] = series
        try {
            series.attachPrimitive(this.primitive)
            this.primitiveAttached = true
        } catch (e) {
            console.warn('DrawingBase: Failed to attach primitive', e)
        }
        return true
    }

    private _attachDrawModePointerListeners() {
        this._removeDrawModePointerListeners()
        if (!this._chartApi || this.mode !== 'DRAW') return
        const elements = [...(this._chartApi.getPaneElements() || [])] as HTMLElement[]
        elements.forEach((el, paneIndex) => {
            const onDown = (e: MouseEvent | TouchEvent) => {
                this._onMouseDownClick(e, paneIndex)
            }
            const onUp = (e: MouseEvent | TouchEvent) => {
                this._onMouseUpClick(e, paneIndex)
            }
            if (this._chart.touchEnabled) {
                el.addEventListener('touchstart', onDown, { passive: true })
                el.addEventListener('touchend', onUp, { passive: true })
                this._drawModePanePointerCleanups.push(() => {
                    el.removeEventListener('touchstart', onDown)
                    el.removeEventListener('touchend', onUp)
                })
            } else {
                el.addEventListener('mousedown', onDown)
                el.addEventListener('mouseup', onUp)
                this._drawModePanePointerCleanups.push(() => {
                    el.removeEventListener('mousedown', onDown)
                    el.removeEventListener('mouseup', onUp)
                })
            }
        })
    }

    private _removeDrawModePointerListeners() {
        this._drawModePanePointerCleanups.forEach((fn) => fn())
        this._drawModePanePointerCleanups = []
    }

    private _onMouseDownClick = (event: MouseEvent | TouchEvent, paneIndexFromEvent?: number) => {
        if (paneIndexFromEvent !== undefined) {
            this._syncDrawPaneFromIndex(paneIndexFromEvent)
        }
        const paneEl = this._paneElement
        if (!paneEl) return
        this._chartBounds = paneEl.getBoundingClientRect()
        this.onMouseDownTime = new Date()
        const touch = (event as TouchEvent)?.touches?.[0]
        const chartX = determineChartX(
            this._chartApi,
            this._chartBounds,
            this._chart.touchEnabled ? (touch?.clientX ?? 0) : ((event as MouseEvent).clientX ?? touch?.clientX ?? 0)
        )
        const chartY = determineChartY(
            this._chartApi,
            this._chartBounds,
            this._chart.touchEnabled ? (touch?.clientY ?? 0) : ((event as MouseEvent).clientY ?? touch?.clientY ?? 0)
        )
        if (this.mode === 'DRAW') {
            this._chart.updateLayoutOptions({
                handleScroll: {
                    pressedMouseMove: false
                }
            })
            const synthetic = {
                hoveredObjectId: undefined,
                hoveredSeries: undefined,
                logical: true,
                paneIndex: this.paneIndex,
                point: {
                    x: chartX,
                    y: chartY
                },
                seriesData: undefined,
                sourceEvent: undefined,
                time: this._chartApi.timeScale().coordinateToTime(chartX ?? 0)
            }
            this._onClick(synthetic as any, false)
        }
    }

    private _onMouseUpClick = (event: MouseEvent | TouchEvent, paneIndexFromEvent?: number) => {
        if (paneIndexFromEvent !== undefined) {
            this._syncDrawPaneFromIndex(paneIndexFromEvent)
        }
        this._chart.updateLayoutOptions({
            handleScroll: {
                pressedMouseMove: true
            }
        })

        // Add second point on mouse up when in drawing mode and first point exists
        // Only trigger for long drags (> 150ms), not for quick clicks
        if (this.mode === 'DRAW' && !this._firstClick && this.onMouseDownTime) {
            const currentTime = new Date()
            const timeDiff = currentTime.getTime() - this.onMouseDownTime.getTime()
            const isLongDrag = timeDiff > 150 // Consider it a long drag if more than 150ms

            if (isLongDrag && this._paneElement) {
                this._chartBounds = this._paneElement.getBoundingClientRect()
                const touch = (event as TouchEvent)?.touches?.[0] || (event as TouchEvent)?.changedTouches?.[0]
                const chartX = determineChartX(this._chartApi, this._chartBounds, this._chart.touchEnabled ? (touch?.clientX ?? 0) : (event as MouseEvent).clientX)
                const chartY = determineChartY(this._chartApi, this._chartBounds, this._chart.touchEnabled ? (touch?.clientY ?? 0) : (event as MouseEvent).clientY)

                if (chartX && chartY) {
                    const mouseUpEvent = {
                        hoveredObjectId: undefined,
                        hoveredSeries: undefined,
                        logical: true,
                        paneIndex: this.paneIndex,
                        point: {
                            x: chartX,
                            y: chartY
                        },
                        seriesData: undefined,
                        sourceEvent: undefined,
                        time: this._chartApi.timeScale().coordinateToTime(chartX ?? 0)
                    }
                    this._onClick(mouseUpEvent as any, false)
                }
            }
        }
    }

    private _onMouseMovePaneIndexTracking = (event: MouseEventParams<Time>) => {
        const paneIndex = event.paneIndex ?? 0
        this.paneIndex = paneIndex
        if (this.subscribedPaneIndex !== paneIndex) {
            this.subscribedPaneIndex = paneIndex
            this._syncDrawPaneFromIndex(paneIndex)
        }
    }

    private _onClick = (event: MouseEventParams, dblClick = false) => {
        if (this.mode === 'DRAW') {
            if (this.referenceDrawingId) return
            const paneIndex = event.paneIndex || 0
            const paneId = this._chart.getPaneIdByIndex(paneIndex)
            const paneElement = this._chartApi.getPaneElements()[paneIndex]
            if (!paneElement) return
            this.setPaneElement(paneId, paneElement)


            const [seriesId, series] = this._chart.getDrawingSeries(paneId)
            if (!seriesId || !series) return
            if (this._firstClick) {
                this._chart.publishDrawingEvent('abort', {
                    id: this.id
                }, true)
                const pane = this._chart.getPane(paneId)
                pane && pane.primary && this._chart.publishDrawingEvent('add', {
                    type: this.getDrawingType(),
                    id: this.id,
                    options: this.options,
                    settings: this._settings,
                    initialState: {}
                })
            }
            this._seriesId = seriesId
            this._paneId = paneId
            this._seriesMap[this.id] = series
            if (!event.point || !event.time || !event.logical) return
            let price = this.coordinateToPrice(event.point.y), eventPoint = { ...event.point }
            if (!price) return
            if (this.magnetMode) {
                price = this.getSnapPoint(event.time, price, event.point.y)
                eventPoint.y = (this.priceToCoordinate(price) || 0) as Coordinate
            }
            // A second click/tap on the exact spot of the previous point (typical on touch:
            // tapping again before moving the cursor) would commit a degenerate zero-length
            // drawing that is invisible and can't be selected or deleted from the chart.
            if (this.ignoreRepeatedPoint && !this._firstClick && this._isSamePointAsLast(event.time, price)) return
            this._onAddPoint(event.time, price, eventPoint)
            if (this._firstClick) {
                if (!this.primitiveAttached && this.primitive) {
                    try {
                        series.attachPrimitive(this.primitive)
                        this.primitiveAttached = true
                    } catch (e) {
                        console.warn('DrawingBase: Failed to attach primitive', e)
                    }
                }
                this._firstClick = false
                this.previewCursorPosition = undefined
                !this.magnetMode && this.addMouseMoveHandler()
                this.updateDrawing()
            }
        } else if ((this._active !== (event.hoveredObjectId === this.id)) || this._softActiveMode) {
            this.toggleFocus(this._softActiveMode ? true : !this._active, this._softActiveMode || !this._active || !event.hoveredObjectId)
        } else if (this._chart.touchEnabled) {
            const item = this.hitTest(event.point.x, event.point.y)
            if(item && item.externalId === this.id) {
                this.toggleFocus(this._softActiveMode ? true : !this._active, this._softActiveMode || !this._active || !item.externalId)
            }
        }
        this._chart.publishEvent(ChartEvents.DRAWING_HOVERED, null)
    }

    /** Same bar and (within a pixel) same price as the last committed point. */
    private _isSamePointAsLast(time: Time, price: number): boolean {
        const last = this.lastAddedPoint
        if (!last || last.x !== time) return false
        const lastY = this.priceToCoordinate(last.y)
        const y = this.priceToCoordinate(price)
        if (lastY == null || y == null) return last.y === price
        return Math.abs(lastY - y) < 1
    }

    _onAddPoint = (time: Time, price: number, coordinates: PointCoordinates, preview?: boolean, external = false): Point | void => {
        this._chart.publishDrawingEvent('add_point', {
            id: this.id,
            time,
            price,
            coordinates,
            preview
        })
        if (this._firstClick && external) this._firstClick = false
        const point = this.onAddPoint(time, price, coordinates, preview)
        if (!preview) {
            if (!point && this.mode === 'DRAW') throw 'Last added point must be returned'
            this.lastAddedPoint = point as Point
        }
        this.updateDrawing()
    }

    onDrawingEnd() {
        this.mode = 'EDIT'
        this.editing = false
        this._stopTouchCursorDrawing()
        this._removeDrawModePointerListeners()
        if (this._chartApi) this._chartApi.unsubscribeCrosshairMove(this._onMouseMovePaneIndexTracking)
        if (this._chartApi) this._chartApi.unsubscribeClick(this._onMouseMovePaneIndexTracking)
        this.removeMouseMoveHandler()
        this.toggleFocus(true, !this.referenceDrawingId, !!this.referenceDrawingId)
        this.onDrawingInactive()
        this.onDrawingComplete(this.id)
        const data={
            chartId:this._chart.id,
            drawing: [{id: this.id,
            type:this.getDrawingType(),
            options: this.options,
            state: this._state,
            paneId:this._paneId,
            seriesId:this._seriesId,
            settings: this._settings,
            }]
        }
        this._chart.historyManager.push(data, `ADD DRAWING (${this.getDrawingType()})`, ActionType.ADD_DRAWING)
        setTimeout(() => {
            this.subscribeClick()
            this.subscribeDblClick()
        }, 200)
    }

    onMouseMove = (event: MouseEventParams<Time>) => {
        const chartX = event.point?.x //determineChartX(this._chartApi, this._chartBounds!, event.clientX);
        const chartY = event.point?.y //determineChartY(this._chartApi, this._chartBounds!, event.clientY);
        const paneIndex = event.paneIndex || 0
        const paneId = this._chart.getPaneIdByIndex(paneIndex)
        if (!chartX || !chartY || paneId !== this._paneId) return
        let price = this.coordinateToPrice(chartY as Coordinate)
        const time = this.coordinateToTime(chartX as Coordinate)
        if (!time || !price) return

        if (this.mode === 'DRAW' && this._firstClick) {
            const snappedX = this.timeToCoordinate(time)
            this.previewCursorPosition = { 
                x: snappedX || chartX, 
                y: chartY 
            }
        }

        // Snap to prices
        if (this.magnetMode) {
            price = this.getSnapPoint(time, price, chartY)

            this._chartApi.setCrosshairPosition(price!, time, this.getSeries())
        }
        if (this.mode === 'DRAW') {
            if (!this._firstClick) {
                this._onAddPoint(time, price!, { x: chartX, y: chartY }, true)
                this.updateDrawing()
            } else {
                this.updateDrawing()
            }
        } else if (this.activeElementId) {
            const [drawingId, handleId] = this.activeElementId.split('__')
            if (handleId) {
                this.editing = true
                this._updateHandle(handleId, time, price!, { x: chartX, y: chartY })
            } else if (drawingId === this.id) {
                this.editing = true
                this.moveDrawing(chartX, chartY)
            }
        }
    }

    _updateHandle(handleId: string, time: Time, price: number, coordinates: PointCoordinates, publish = true, syncMode = false) {
        this.updateHandle(handleId, time, price!, coordinates, syncMode)
        this.updateDrawing()
        this.onDrawingUpdate(this.id)
        publish && this._chart.publishDrawingEvent('update_handle', {
            id: this.id,
            handleId,
            time,
            price,
            coordinates,
            referenceId: this.referenceDrawingId
        }, true)
    }

    publishSyncStateEvent() {
        this._chart.publishDrawingEvent('update_state', {
            id: this.id,
            referenceId: this.referenceDrawingId,
            state: this.getSyncState()
        }, true)
    }

    getSnapPoint(time: Time, price: BarPrice, chartY: number) {
        const logical = this.timeToLogical(time)
        let snapPrice = price
        if (logical) {
            const data = this.getSeries().data()
            const candle = data[logical]
            if (candle) {
                const { open, high, low, close } = candle
                // opposite becuase of inverted canvas coordinates
                const max = this.priceToCoordinate(low) || 0
                const min = this.priceToCoordinate(high) || 0
                if (chartY >= min - SNAP_THRESHOLD && chartY <= max + SNAP_THRESHOLD) {
                    let updatePrice = close;
                    [open, high, low].forEach((p) => {
                        const y = this.priceToCoordinate(p) || 0
                        if (Math.abs(y - chartY) < Math.abs((this.priceToCoordinate(updatePrice) || 0) - chartY)) {
                            updatePrice = p
                        }
                    })
                    snapPrice = updatePrice
                }
            }
        }

        return snapPrice

    }

    _onMouseDown = (event: MouseEvent | TouchEvent) => {        
        this._chartBounds = this.getPaneElement().getBoundingClientRect()
        const touch = (event as TouchEvent)?.touches?.[0]
        const chartX = determineChartX(this._chartApi, this._chartBounds, this._chart.touchEnabled ? (touch?.clientX??0) : ((event as MouseEvent)?.clientX??0))
        const chartY = determineChartY(this._chartApi, this._chartBounds, this._chart.touchEnabled ? (touch?.clientY??0) : ((event as MouseEvent)?.clientY??0))
        if (!chartX || !chartY) return
        this.moveStartMouseLocation = {
            x: chartX,
            y: chartY
        }
        this.moveStartHandles = deepClone(this.getHandles() as any)
        const hoveredItem = this._hitTest(chartX, chartY)
        if (hoveredItem) {
            this._mousePressed = true
            this.activeElementId = hoveredItem.externalId
            this._chartApi.applyOptions({
                handleScroll: {
                    pressedMouseMove: false
                }
            })
            this.addMouseMoveHandler()
            this.addGlobalMouseUpHandler()
        }
    }
    _globalOnMouseUp = () => {
        if(this.editing) {
            this.editing = false
            this.onEditEnding()
        }
        this._mousePressed = false
        this.activeElementId = ''
        this.moveStartMouseLocation = undefined
        this.moveStartHandles = undefined
        this._chartApi.applyOptions({
            handleScroll: {
                pressedMouseMove: true
            }
        })
        this.removeMouseMoveHandler()
        this.removeGlobalMouseUpHandler()
        this.onGlobalMouseUp();
    }

    getPaneAxisViewConfig(id: string) {
        return this.axisViewManager.getPaneAxisViewConfig(id)
    }

    getPaneViews = (): DrawingPaneView[] => {
        let paneViews = this.paneViews
        const paneViewIDs = paneViews.map(v => v.id)
        const handles = this._getHandles()
        handles.forEach((h) => {
            if (paneViewIDs.indexOf(h.id) === -1 && !h.hidden) {
                paneViews = [...paneViews, new DrawingHandlePaneView({ id: h.id, drawing: this })]
            }
        })
        return paneViews
    }
    getPriceAxisPaneViews = () => {
        return this.axisViewManager.getPriceAxisPaneViews()
    }
    getTimeAxisPaneViews = () => {
        return this.axisViewManager.getTimeAxisPaneViews()
    }

    getPriceAxisLabels(): DrawingAxisLabelView[] {
        return this.axisViewManager.getPriceAxisLabels()
    }

    getTimeAxisLabels(): DrawingAxisLabelView[] {
        return this.axisViewManager.getTimeAxisLabels()
    }

    moveDrawing = (x: number, y: number) => {
        if (!this.moveStartMouseLocation || !this.moveStartHandles) {
            return
        }

        const { x: startX, y: startY } = this.moveStartMouseLocation
        const xStartIndex = this._chartApi.timeScale().coordinateToLogical(startX)
        const currentIndex = this._chartApi.timeScale().coordinateToLogical(x)
        if (!currentIndex || !xStartIndex) return
        const deltaIndex = currentIndex - xStartIndex
        const deltaY = y - startY
        let update = true
        const updateHandles = this.moveStartHandles.map((h) => {

            const updatedHandle = this.getUpdatedPoint(h, deltaIndex, deltaY, this.getSeries())
            if (!updatedHandle) {
                update = false
                return h
            }
            return updatedHandle
        })

        if (update) {
            this._updateHandles(updateHandles)
        }
    }

    _updateHandles(points: Point[], publish = true) {
        this.updateHandles(points)
        this.updateDrawing()
        this.onDrawingUpdate(this.id)
        publish && this._chart.publishDrawingEvent('move', {
            id: this.id,
            handles: points,
            referenceId: this.referenceDrawingId
        }, true)
    }

    getUpdatedPoint(p: Point, deltaIndex: number, deltaY: number, series: ISeriesApi<any>): Point | null {
        const xCoord = p.coordinates ? p.coordinates.x : this.timeToCoordinate(p.x)
        let yCoord = p.coordinates ? p.coordinates.y : series.priceToCoordinate(p.y) as number
        if (!xCoord || !yCoord) return null
        const timescale = this._chartApi.timeScale()
        let xIndex = timescale.coordinateToLogical(xCoord) as number
        if (!xIndex) return null
        xIndex += deltaIndex

        yCoord += deltaY

        const newXCoord = timescale.logicalToCoordinate(xIndex as Logical)
        const newYCoord = series.coordinateToPrice(yCoord)
        if (!newXCoord || !newYCoord) return null
        const newTime = timescale.coordinateToTime(newXCoord)
        if (!newTime) return null

        const newPoint: Point = {
            ...p,
            x: newTime,
            y: newYCoord
        }

        if (p.coordinates) {
            newPoint.coordinates = {
                x: newXCoord,
                y: yCoord
            }
        }
        return newPoint
    }

    getHandlePoint(id: string) {
        return this.getHandles().find((v => v.id === id))
    }

    updateDrawing(recalculateHandles = true) {
        this._invalidateSeriesCache()
        recalculateHandles && this.recalculateHandles()
        this.axisViewManager.updateAxisConfigs()
        this.primitive?.requestUpdate()
    }

    _endDrawing(publish = true) {
        this.endDrawing()
        publish && this._chart.publishDrawingEvent('end', {
            id: this.id,
            referenceId: this.referenceDrawingId
        })
    }

    _hitTest(x: number, y: number): PrimitiveHoveredItem | null {
        if (this.mode === 'DRAW') return null

        let hoveredItem: PrimitiveHoveredItem = {
            externalId: '',
            zOrder: 'normal',
            cursorStyle: 'pointer'
        }
        if (this._active) {
            const handleId = this._checkHandleHovered(x, y)
            if (handleId) {
                hoveredItem.externalId = handleId
            }
        }
        if (!hoveredItem.externalId) {
            let item = this.hitTest(x, y)
            if (item) {
                hoveredItem = item
            }
        }
        return hoveredItem.externalId ? {
            ...hoveredItem,
            cursorStyle: hoveredItem.cursorStyle ?? 'pointer'
        } : null
    }

    _checkHandleHovered(x: number, y: number): string {
        const handles = this.getHandles()
        let handleId = ''
        // Handles are drawn at HANDLER_RADIUS; a finger needs a larger grab area than the drawn circle.
        const hitRadius = isCoarsePointer() ? getHitThreshold() : HANDLER_RADIUS + HANDLER_STROKE_WIDTH
        handles.forEach((h) => {
            let hx = this.timeToCoordinate(h.x)
            let hy = this.priceToCoordinate(h.y)

            if (h.coordinates) {
                hx = h.coordinates.x as Coordinate
                hy = h.coordinates.y as Coordinate
            }
            if (!hx || !hy) return
            if (Math.pow(hx - x, 2) + Math.pow(hy - y, 2) <= Math.pow(hitRadius, 2) && !h.hidden) {
                handleId = this.id + '__' + h.id
            }
        })

        return handleId
    }

    getTimescaleData = (): Array<{time: Time}> => {
        return [...this._chart.getRenderedPrimaryData(), ...this._chart.emptyTimeSlots]
    }

    private _getPrimarySeriesForCoordinateConversion(): ISeriesApi<any> | null {
        // Check if compare mode changed (which affects which series to use)
        const currentCompareMode = this._chart.compareMode;
        if (this._lastChartCompareMode !== currentCompareMode) {
            this._invalidateSeriesCache();
            this._lastChartCompareMode = currentCompareMode;
        }

        // Use cached series if available and cache is valid
        if (!this._cacheInvalidated && this._cachedPrimarySeries) {
            return this._cachedPrimarySeries
        }
        // Try to get the primary series that participates in percentage scaling
        const primarySeriesGroup = this._chart.getPrimarySeriesGroup()
        if (primarySeriesGroup && primarySeriesGroup.series.length > 0) {
            const primarySeriesConfig = primarySeriesGroup.series[0]
            const primarySeries = primarySeriesGroup.chartSeries[primarySeriesConfig?.id]
            if (primarySeries && primarySeries.seriesApi) {
                this._cachedPrimarySeries = primarySeries.seriesApi
                this._cacheInvalidated = false
                return this._cachedPrimarySeries
            }
        }
        // Cache the blank series as fallback
        const blankSeries = this._chart.getBlankSeries()
        this._cachedPrimarySeries = blankSeries || null
        this._cacheInvalidated = false
        return this._cachedPrimarySeries
    }

    private _invalidateSeriesCache() {
        this._cacheInvalidated = true
        this._cachedPrimarySeries = null
    }

    coordinateToPrice(y: Coordinate) {
        const srs = this.getSeries() || this._getPrimarySeriesForCoordinateConversion()
        if (!srs) return null
        return srs.coordinateToPrice(y)
    }

    coordinateToTime(x: Coordinate) {
        const timeScale = this._chartApi.timeScale()
        return timeScale.coordinateToTime(x)
    }

    priceToCoordinate(price: number): Coordinate | null {
        const srs = this.getSeries() || this._getPrimarySeriesForCoordinateConversion()
        if (!srs) return null
        return srs.priceToCoordinate(price)
    }

    timeToLogical(time: Time): number | null {
        const timeScale = this._chartApi.timeScale()

        const coord = this.timeToCoordinate(time)

        if (!coord) return null

        return timeScale.coordinateToLogical(coord)
    }

    timeToCoordinate(time: Time, exact?: boolean): Coordinate | null {
        const timeScale = this._chartApi.timeScale()
        const data = this.getTimescaleData();
        if (!data.length) return null
        const index = getTimeIndex(data, time as number)
        if (!Number.isFinite(index)) return null

        const newTime1 = timeScale.logicalToCoordinate(index as Logical)
        if (!exact) {
            return newTime1
        }
        if (data[index].time == time) {
            return newTime1
        }
        let newTime2 = null
        if (index + 1 < data.length) {
            newTime2 = timeScale.logicalToCoordinate(index + 1 as Logical)
        }
        if (!newTime2 || !newTime1) {
            return newTime1
        }
        const fraction = (time as number - (data[index].time as number)) / ((data[index + 1].time as number) - (data[index].time as number))
        return (newTime1 + (newTime2 - newTime1) * fraction) as Coordinate
    }

    logicalToTime(index: number): Time | null {
        const data = this.getTimescaleData();
        if (index <0 || index >= data.length - 1) return null;
        return data[index].time
    }

    getPriceScaleWidth() {
        const srs = this.getSeries() as { priceScale?: () => { width: () => number } } | undefined
        if (srs && typeof srs.priceScale === 'function') {
            try {
                return srs.priceScale().width()
            } catch {
                /* series detached */
            }
        }
        return this._chartApi.priceScale('right').width()
    }

    getTimeScaleWidth() {
        return this._chartApi.timeScale().width()
    }

    formatPrice(price: number) {
        const formatter = this.getSeries().priceFormatter()
        return formatter.format(price)
    }

    formatTime(time: Time) {
        return this._chartApi.timeScale().formatDateTime(time)
    }

    getAutoscaleInfo(_startTimePoint: Logical, _endTimePoint: Logical): AutoscaleInfo | null {
        return null
    }

    updateSettings(settings: Partial<C>, publish = true) {
        this._settings = {
            ...this._settings,
            ...settings
        }
        this.onSettingsUpdate(settings)
        this.updateDrawing()
        this.onDrawingUpdate(this.id)
        publish && this._chart.publishDrawingEvent('update_settings', {
            id: this.id,
            settings,
            referenceId: this.referenceDrawingId
        }, true)
    }

    updateState(state: Partial<S>) {
        this._state = {
            ...this._state,
            ...state
        }
        this.updateDrawing()
    }

    getSettingsValues(): C {
        // override in the extended class with dynamically changing values like coordinates
        return { ...this._settings }
    }

    openDrawingSettings() {
        if (
            this.options.drawingType !== 'contractSwitch' &&
            this.options.drawingType !== 'corporateAction' &&
            this.options.drawingType !== 'companyEvent' &&
            this.options.drawingType !== 'newsArticle' &&
            this.options.drawingType !== 'chartAlertsOnChart'
        ) {
            this._chart.publishEvent(ChartEvents.OPEN_DRAWING_SETTINGS, {
                id: this.id,
                open: true
            })
        }
    }

    closeDrawingSettings() {
        this._chart.publishEvent(ChartEvents.OPEN_DRAWING_SETTINGS, {
            id: this.id,
            open: false
        })
    }

    destroy(publish = true, pushToHistory = true, chartTypeToHistory=false) {
        this._destroyed = true
        this._invalidateSeriesCache()
        const data = {
            chartId:this._chart.id,
            drawing: [{id: this.id,
                type:this.getDrawingType(),
                options: this.options,
                state: this._state,
                paneId:this._paneId,
                seriesId:this._seriesId,
                settings: this._settings,
            }]
        }
        if (pushToHistory && chartTypeToHistory) {
            this._chart.historyManager.push(data, `REMOVE DRAWING (${this.getDrawingType()})`, ActionType.REMOVE_DRAWING);
        }
        this._stopTouchCursorDrawing()
        this._removeDrawModePointerListeners()
        if (this._chartApi) this._chartApi.unsubscribeCrosshairMove(this._onMouseMovePaneIndexTracking)
        this.removeMouseMoveHandler()
        if (this.primitive) {
            const series = this.getSeries()
            if (series) {
                try {
                    series.detachPrimitive(this.primitive)
                } catch (e) {
                    console.warn('DrawingBase: Failed to detach primitive during destroy', e)
                }
            }
        }
        this.removeMouseLeaveHandler()
        this.updateDrawing(false)
        this.primitive = undefined
        this.resizeObserver.disconnect()
        this.unsubscribeClick()
        this.unsubscribeDblClick()
        if (this._chartApi) this._chartApi.unsubscribeClick(this._onMouseMovePaneIndexTracking)
        this.onDestroy(this.id, publish)
    }

    // override
    getDrawingType(): DrawingTypes {
        throw `Method not defined: getDrawingType`
    }

    getDisplayType = (): DrawingDisplayType => {
        return this.displayType;
    }

    endDrawing = (): void => {
    }
    onAddPoint = (time: Time, price: number, coordinates: PointCoordinates, preview?: boolean, doubleClick?: boolean): Point | void => {
    }
    onSettingsUpdate = (settings: Partial<C>) => {
    }
    updateHandle = (id: string, time: Time, price: number, coordinate: PointCoordinates, syncMode?: boolean) => {
    }
    recalculateHandles = () => {
    }

    loadIntialState() {
    }

    onEditEnding() {
        let drawingType: string | undefined
        try {
            drawingType = this.getDrawingType() as string
        } catch {
            drawingType = this.options?.drawingType as string | undefined
        }
        if (!isAlertableDrawingType(drawingType)) return
        this._chart.publishEvent(ChartEvents.CHART_DRAWING_ALERT_SYNC, { drawingId: this.id })
    }

    /** Stroke/fill color used for the linked-alert icon when the alert is active. */
    getDrawingStrokeColor(): string {
        const settings = this._settings as Record<string, unknown>
        const asColor = (value: unknown): string | undefined => {
            if (!value || typeof value !== 'object') return undefined
            const color = (value as { color?: string }).color
            return typeof color === 'string' && color ? color : undefined
        }
        return (
            asColor(settings.lineColor) ||
            asColor(settings.strokeColor) ||
            asColor(settings.labelColor) ||
            primaryDrawingColor
        )
    }

    /** Chart point used as the alert-icon anchor (drawing start / first handle). */
    getAlertIconAnchorPoint(): Point | undefined {
        const handles = this.getHandles().filter((h) => h && !h.hidden && h.x != null && h.y != null)
        if (handles.length) {
            const byId =
                handles.find((h) => h.id === 'p1' || h.id === 'point1') ??
                handles[0]
            return byId
        }
        const settings = this.getSettingsValues() as { point1?: Point }
        if (settings?.point1?.x != null && settings.point1.y != null) {
            return settings.point1
        }
        return undefined
    }

    /**
     * TradingView-style: alarm icon above the drawing start point when an alert is linked.
     * Active → drawing color; inactive → theme secondary text.
     */
    drawLinkedAlertIcon(scope: BitmapCoordinatesRenderingScope) {
        if (this.mode === 'DRAW' || !this.visible) return
        let drawingType: string | undefined
        try {
            drawingType = this.getDrawingType() as string
        } catch {
            drawingType = this.options?.drawingType as string | undefined
        }
        if (!isAlertableDrawingType(drawingType)) return

        const alerts = this._chart.dataManager?.chartAlertsOnChart?.alerts ?? []
        const linkedAlert = findUserAlertForDrawingId(alerts, this.id)
        if (!linkedAlert) return

        const anchor = this.getAlertIconAnchorPoint()
        if (!anchor) return

        const xMedia = this.timeToCoordinate(anchor.x)
        const yMedia = this.priceToCoordinate(anchor.y)
        if (xMedia == null || yMedia == null) return

        const hRatio = this.horizontalPixelRatio || window.devicePixelRatio || 1
        const vRatio = this.verticalPixelRatio || window.devicePixelRatio || 1
        const cx = scaledCoordinate(xMedia, hRatio)
        const cy = scaledCoordinate(yMedia, vRatio)
        // Place icon above the start point (TradingView-style).
        const iconY = cy - 16 * vRatio

        const active = linkedAlert.status === UserAlert_Status.ACTIVE
        const color = active
            ? this.getDrawingStrokeColor()
            : (this._chart.theme?.palette?.text?.secondary || '#787B86')

        drawDrawingAlertIcon(scope.context, cx, iconY, color, hRatio, vRatio)
    }

    draw(scope: BitmapCoordinatesRenderingScope, target: CanvasRenderingTarget2D) {
    }

    drawPreviewCursorDot(scope: BitmapCoordinatesRenderingScope) {
        if (!this.previewCursorPosition) return
        
        const ctx = scope.context
        const x = scaledCoordinate(this.previewCursorPosition.x, this.horizontalPixelRatio)
        const y = scaledCoordinate(this.previewCursorPosition.y, this.verticalPixelRatio)
        
        if (!x || !y) return

        ctx.beginPath()
        ctx.strokeStyle = primaryDrawingColor
        ctx.fillStyle = this._chart.theme?.palette?.background?.default || '#ffffff'
        ctx.lineWidth = HANDLER_STROKE_WIDTH * window.devicePixelRatio
        ctx.arc(x, y, HANDLER_RADIUS * window.devicePixelRatio, 0, 2 * Math.PI)
        ctx.fill()
        ctx.stroke()
        ctx.closePath()
    }

    updateHandles(points: Point[]) {
    }

    hitTest(x: number, y: number): PrimitiveHoveredItem | null {
        return null
    }

    getSyncState = (): Partial<S> => {
        return {}
    }

    _onDrawingActive() {
        this.onDrawingActive(this.getDrawingType())
    }

    onGlobalMouseUp = () => {
    }

    getPriceAxisLabelPoints = (): LabelAxisViewConfig<number>[] => []
    getTimeAxisLabelPoints = (): LabelAxisViewConfig<Time>[] => []
    getHandles = (): Point[] => []
    getAvailableSettings = (): DrawingSettingGroup<C, SettingValues>[] => []
    getMiniSettings = (): (keyof C)[] => []
}

export default DrawingBase